var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import FocusOutlinePositionWatcherEnum from '../../enums/FocusOutlinePositionWatcherEnum.js';
import FocusOutlineStyleTypeEnum from '../../enums/FocusOutlineStyleTypeEnum.js';
import FocusOutlineStyle from './FocusOutlineStyle.js';
const OUTLINE_ELEMENT_SET_STYLE_PROPERTIES = ['top', 'left', 'width', 'height'];
const OUTLINE_ELEMENT_CLEAR_STYLE_PROPERTIES = OUTLINE_ELEMENT_SET_STYLE_PROPERTIES.concat([
    'right',
    'position'
]);
/**
 * This component should be used when focusing an element.
 *
 */
export default class FocusOutline extends KompisElement {
    constructor() {
        super(...arguments);
        // Force the outline to be visible.
        this.focused = false;
        // Sets the styling of display for the component, either acting as a block or inline element.
        this.type = FocusOutlineStyleTypeEnum.block;
        // Invert color of the outline.
        this.invertedColors = false;
        // Sets the behaviour of the position watcher. The position watcher is useful when the component is inside an overflown element where it can become cropped. When enabled it will be set itself to be position fixed and update the position of itself live. However, updating the position live can be slow and result in lag on slower computers. Be wary of using 'auto' or 'enabled' if the component have any ancestors with transform styling.
        this.positionWatcher = FocusOutlinePositionWatcherEnum.auto;
        this.outlineElement = null;
        this.hasFocus = false;
        this.blockFocus = false;
        this.listeners = {
            mousedown: this.onMouseDown.bind(this),
            focusin: this.onFocusIn.bind(this),
            focusout: this.onFocusOut.bind(this)
        };
        this.position = {
            top: 0,
            left: 0,
            width: 0,
            height: 0
        };
        this.resizeObserver = new ResizeObserver(() => this.requestUpdate());
    }
    render() {
        return html `<style>
				${FocusOutlineStyle.getStyle({
            theme: this.theme,
            hasFocus: this.hasFocus,
            styleType: this.type,
            invertedColors: this.invertedColors
        })}</style
			>${this.getOutlineType()}<slot></slot>`;
    }
    update() {
        super.update();
        this.outlineElement =
            this.outlineElement || this.shadowRoot?.querySelector('.outlineContainer') || null;
        if (!this.outlineElement) {
            return;
        }
        if (this.hasFocus && this.type === FocusOutlineStyleTypeEnum.block) {
            if (this.usePositionWatcher()) {
                this.position = {
                    top: 0,
                    left: 0,
                    width: 0,
                    height: 0
                };
                this.outlineElement.style.right = 'auto';
                this.outlineElement.style.position = 'fixed';
                this.watchPosition();
            }
        }
        else {
            for (const property of OUTLINE_ELEMENT_CLEAR_STYLE_PROPERTIES) {
                if (!this.outlineElement?.style) {
                    return;
                }
                this.outlineElement.style.removeProperty(property);
            }
        }
    }
    connectedCallback() {
        super.connectedCallback();
        this.addEventListener('mousedown', this.listeners.mousedown);
        this.addEventListener('focusin', this.listeners.focusin);
        this.addEventListener('focusout', this.listeners.focusout);
        this.resizeObserver.observe(this);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener('mousedown', this.listeners.mousedown);
        this.removeEventListener('focusin', this.listeners.focusin);
        this.removeEventListener('focusout', this.listeners.focusout);
        this.resizeObserver.disconnect();
        this.outlineElement = null;
        this.hasFocus = false;
    }
    shouldUpdate(changedProperties) {
        if (changedProperties.has('focused')) {
            if (this.focused) {
                this.hasFocus = true;
            }
            else {
                this.hasFocus = false;
            }
        }
        return true;
    }
    /**
     * Depending on inline or block, it does separate things.
     */
    getOutlineType() {
        if (this.type === FocusOutlineStyleTypeEnum.block) {
            return this.getOutline();
        }
    }
    getOutline() {
        return html `
			<div class="outlineContainer">
				<div class="outline"></div>
			</div>
		`;
    }
    /**
     * Triggered on focus in.
     */
    onFocusIn() {
        if (!this.hasFocus && !this.blockFocus) {
            this.hasFocus = true;
            this.update();
        }
        this.blockFocus = false;
    }
    /**
     * Triggered on focus out.
     */
    onFocusOut() {
        if (this.hasFocus) {
            this.hasFocus = false;
            this.update();
        }
    }
    /**
     * Triggered on mouse down.
     */
    onMouseDown() {
        this.blockFocus = true;
    }
    /**
     * Check if position watcher should be used.
     *
     * @returns "true" if position watcher should be used.
     */
    usePositionWatcher() {
        switch (this.positionWatcher) {
            case FocusOutlinePositionWatcherEnum.auto:
                return this.isInsideOverflowElement();
            case FocusOutlinePositionWatcherEnum.enabled:
                return true;
            case FocusOutlinePositionWatcherEnum.disabled:
                return false;
        }
    }
    /**
     * Check if FocusOutline is inside an overflow element.
     *
     * @returns "true" if inside an overflow element.
     */
    isInsideOverflowElement() {
        let parent = this.parentNode;
        while (parent) {
            if (parent instanceof HTMLElement) {
                const computedStyle = window.getComputedStyle(parent);
                if (computedStyle.overflow !== 'visible' ||
                    computedStyle.overflowY !== 'visible' ||
                    computedStyle.overflowX !== 'visible') {
                    return true;
                }
            }
            parent = parent.parentNode || parent.host?.parentNode;
            if (parent === document.body || parent === document.documentElement) {
                return false;
            }
            const parentShadow = parent.shadowRoot;
            const parentShadowSlot = parentShadow?.querySelector('slot');
            if (parent && parentShadowSlot) {
                parent = parentShadowSlot.parentNode;
            }
        }
        return false;
    }
    /**
     * Watches the position.
     */
    watchPosition() {
        if (!this.outlineElement || !this.usePositionWatcher || !this.hasFocus) {
            return;
        }
        const boundingClientRect = this.getBoundingClientRect();
        for (const property of OUTLINE_ELEMENT_SET_STYLE_PROPERTIES) {
            if (this.position[property] !== boundingClientRect[property]) {
                this.position[property] = boundingClientRect[property];
                this.outlineElement.style[property] = boundingClientRect[property] + 'px';
            }
        }
        window.requestAnimationFrame(this.watchPosition.bind(this));
    }
}
__decorate([
    property({ type: Object, required: true })
], FocusOutline.prototype, "theme", void 0);
__decorate([
    property({ type: Boolean })
], FocusOutline.prototype, "focused", void 0);
__decorate([
    property({ type: String })
], FocusOutline.prototype, "type", void 0);
__decorate([
    property({ type: Boolean })
], FocusOutline.prototype, "invertedColors", void 0);
__decorate([
    property({ type: String })
], FocusOutline.prototype, "positionWatcher", void 0);
//# sourceMappingURL=FocusOutline.js.map